import { Checkbox } from '@consigli/facade';
import { useTranslation } from 'react-i18next';
import { FiClock, FiRepeat, FiUsers } from 'react-icons/fi';

import { OutlookMeeting } from './outlook-import.types';

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatTimeRange = (
  startIso: string,
  endIso: string,
  durationMinutes: number,
  locale: string,
  minutesLabel: string,
): string => {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const start = new Date(startIso).toLocaleTimeString(locale, opts);
  const end = new Date(endIso).toLocaleTimeString(locale, opts);
  return `${start}–${end} (${durationMinutes} ${minutesLabel})`;
};

interface OutlookMeetingRowProps {
  meeting: OutlookMeeting;
  selected: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

export const OutlookMeetingRow = ({
  meeting,
  selected,
  disabled = false,
  onToggle,
}: OutlookMeetingRowProps) => {
  const { t, i18n } = useTranslation();

  return (
    <div
      className={`flex items-center gap-4 rounded-lg border p-4 transition-colors ${
        selected ? 'border-brand-default bg-blue-50/50' : 'border-gray-200'
      } ${disabled ? 'opacity-60' : 'hover:border-gray-300'}`}
    >
      <Checkbox
        id={`outlook-meeting-${meeting.id}`}
        checked={selected}
        onChange={onToggle}
        disabled={disabled}
      />

      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="flex flex-1 items-center justify-between gap-4 text-left disabled:cursor-not-allowed"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-base font-semibold text-gray-900">{meeting.title}</h4>
            {meeting.isRecurring && (
              <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-600">
                <FiRepeat size={12} />
                {t('meeting-secretary.outlook-import.recurring-tag')}
              </span>
            )}
            {disabled && (
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                {t('meeting-secretary.outlook-import.already-imported')}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <FiClock size={14} />
              {formatTimeRange(
                meeting.startIso,
                meeting.endIso,
                meeting.durationMinutes,
                i18n.language,
                t('meeting-secretary.meetings.minutes'),
              )}
            </span>
            <span className="inline-flex items-center gap-1">
              <FiUsers size={14} />
              {t('meeting-secretary.meetings.participants-count', { count: meeting.attendeeCount })}
            </span>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600">
            {getInitials(meeting.organizerName)}
          </div>
          <div className="text-right">
            <div className="max-w-[160px] truncate text-sm font-medium text-gray-900">
              {meeting.organizerName}
            </div>
            <div className="text-xs text-gray-500">
              {t('meeting-secretary.outlook-import.organizer')}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};
